import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ArrowLeft, Tag, Power, Edit, X } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  code: "", type: "percent", value: 10, min_order_value: 0,
  max_redemptions: "", per_customer_limit: "", expires_at: "",
  categories: [], active: true,
};

function CouponForm({ existing, onSave, onCancel, saving, categories }) {
  const [form, setForm] = useState(existing ? {
    ...existing,
    max_redemptions: existing.max_redemptions ?? "",
    per_customer_limit: existing.per_customer_limit ?? "",
    expires_at: existing.expires_at ? existing.expires_at.slice(0, 16) : "",
  } : EMPTY_FORM);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleCategory = (cat) => set("categories", form.categories.includes(cat)
    ? form.categories.filter(c => c !== cat)
    : [...form.categories, cat]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      value: form.type === "free_shipping" ? 0 : Number(form.value),
      min_order_value: Number(form.min_order_value || 0),
      max_redemptions: form.max_redemptions === "" ? null : Number(form.max_redemptions),
      per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      categories: form.categories,
      active: form.active,
    };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="coupon-form" className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 mb-8 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Code</Label>
          <Input
            data-testid="coupon-code"
            required
            disabled={!!existing}
            value={form.code}
            onChange={(e) => set("code", e.target.value.toUpperCase())}
            placeholder="WELCOME10"
            className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono uppercase"
          />
        </div>
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Type</Label>
          <select
            data-testid="coupon-type"
            value={form.type}
            onChange={(e) => set("type", e.target.value)}
            className="w-full bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] focus:border-[#39FF14] outline-none p-2 font-mono text-sm"
          >
            <option value="percent">Percentage off</option>
            <option value="amount">Fixed amount off</option>
            <option value="free_shipping">Free shipping</option>
          </select>
        </div>
      </div>

      {form.type !== "free_shipping" && (
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">
            {form.type === "percent" ? "Percent (1-100)" : "Dollar amount"}
          </Label>
          <Input
            data-testid="coupon-value"
            type="number" min={1} step={form.type === "percent" ? 1 : 0.01}
            value={form.value}
            onChange={(e) => set("value", e.target.value)}
            className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Min order ($)</Label>
          <Input
            data-testid="coupon-min-order"
            type="number" min={0} step={0.01}
            value={form.min_order_value}
            onChange={(e) => set("min_order_value", e.target.value)}
            className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
          />
        </div>
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Max total uses</Label>
          <Input
            data-testid="coupon-max-redemptions"
            type="number" min={1}
            value={form.max_redemptions}
            onChange={(e) => set("max_redemptions", e.target.value)}
            placeholder="Unlimited"
            className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
          />
        </div>
        <div>
          <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Per-customer limit</Label>
          <Input
            data-testid="coupon-per-customer"
            type="number" min={1}
            value={form.per_customer_limit}
            onChange={(e) => set("per_customer_limit", e.target.value)}
            placeholder="Unlimited"
            className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
          />
        </div>
      </div>

      <div>
        <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Expires at (optional)</Label>
        <Input
          data-testid="coupon-expires"
          type="datetime-local"
          value={form.expires_at}
          onChange={(e) => set("expires_at", e.target.value)}
          className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
        />
      </div>

      <div>
        <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Restrict to categories (none = all)</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {categories.map(c => (
            <button
              key={c.key}
              type="button"
              onClick={() => toggleCategory(c.key)}
              data-testid={`coupon-cat-${c.key}`}
              className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-colors ${
                form.categories.includes(c.key)
                  ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
                  : "border-[#1A1A1A] text-[#A1A1A1] hover:border-[#39FF14]/50"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="coupon-active"
          type="checkbox"
          checked={form.active}
          onChange={(e) => set("active", e.target.checked)}
          className="accent-[#39FF14]"
          data-testid="coupon-active"
        />
        <label htmlFor="coupon-active" className="text-[#A1A1A1] font-mono text-xs uppercase tracking-wider cursor-pointer">Active</label>
      </div>

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} data-testid="coupon-save" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider px-6">
          {saving ? "Saving..." : existing ? "Update" : "Create"}
        </Button>
        <Button type="button" onClick={onCancel} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider">
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function AdminCouponsPage() {
  const { user, loading: authLoading } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  const fetchCoupons = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/coupons`);
      setCoupons(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchCoupons();
  }, [user, authLoading, navigate, fetchCoupons]);

  const handleSave = async (payload) => {
    setSaving(true);
    setFeedback("");
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/coupons/${editing.id}`, payload);
        setFeedback(`Updated ${payload.code}`);
      } else {
        await axios.post(`${API}/api/admin/coupons`, payload);
        setFeedback(`Created ${payload.code}`);
      }
      setShowForm(false);
      setEditing(null);
      await fetchCoupons();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setFeedback(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete ${coupon.code}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/coupons/${coupon.id}`);
      setFeedback(`Deleted ${coupon.code}`);
      await fetchCoupons();
    } catch (e) {
      setFeedback("Delete failed");
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/api/admin/coupons/${coupon.id}`, { active: !coupon.active });
      await fetchCoupons();
    } catch (e) {
      setFeedback("Toggle failed");
    }
  };

  const describeCoupon = (c) => {
    if (c.type === "percent") return `${c.value}% off`;
    if (c.type === "amount") return `$${c.value.toFixed(2)} off`;
    return "Free shipping";
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="coupons-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Discount Codes
            </h1>
          </div>
          {!showForm && (
            <Button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="new-coupon-btn"
              className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider"
            >
              <Plus className="h-3 w-3 mr-2" /> New Code
            </Button>
          )}
        </div>

        {feedback && (
          <div className="mb-6 p-3 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs font-mono flex items-center justify-between" data-testid="coupons-feedback">
            <span>{feedback}</span>
            <button onClick={() => setFeedback("")} className="text-[#A1A1A1] hover:text-[#39FF14]"><X className="h-3 w-3" /></button>
          </div>
        )}

        {showForm && (
          <CouponForm
            existing={editing}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            saving={saving}
            categories={categories}
          />
        )}

        <div className="bg-[#0D0D0D] border border-[#1A1A1A]" data-testid="coupons-list">
          {coupons.length === 0 ? (
            <p className="p-6 text-[#A1A1A1] text-sm font-mono text-center">No discount codes yet. Create one to get started.</p>
          ) : (
            coupons.map(c => (
              <div key={c.id} data-testid={`coupon-${c.code}`} className="px-4 py-4 border-b border-[#1A1A1A] last:border-b-0 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Tag className={`h-4 w-4 shrink-0 ${c.active ? "text-[#39FF14]" : "text-[#A1A1A1]"}`} strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-[#F2F2F2]">{c.code}</span>
                      <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${c.active ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-[#1A1A1A] text-[#A1A1A1]"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                      <span className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">{describeCoupon(c)}</span>
                    </div>
                    <div className="font-mono text-[10px] text-[#A1A1A1] mt-1">
                      Used {c.redemption_count}{c.max_redemptions ? `/${c.max_redemptions}` : ""}
                      {c.min_order_value > 0 && <span> · Min ${c.min_order_value.toFixed(2)}</span>}
                      {c.per_customer_limit && <span> · {c.per_customer_limit}/customer</span>}
                      {c.expires_at && <span> · Expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                      {c.categories?.length > 0 && <span> · {c.categories.join(", ")}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleActive(c)} title={c.active ? "Disable" : "Enable"} data-testid={`toggle-${c.code}`} className="p-2 text-[#A1A1A1] hover:text-[#39FF14] transition-colors">
                    <Power className="h-4 w-4" />
                  </button>
                  <button onClick={() => { setEditing(c); setShowForm(true); }} title="Edit" data-testid={`edit-${c.code}`} className="p-2 text-[#A1A1A1] hover:text-[#39FF14] transition-colors">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(c)} title="Delete" data-testid={`delete-${c.code}`} className="p-2 text-[#A1A1A1] hover:text-red-400 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
